/**
 * Delegated ("shadow") locations — the shared predicate and filter.
 *
 * A location is DELEGATED when `delegated_to_location_id` is set: its fulfilment
 * is routed to a shared 3PL location the org is an active client of, so nothing
 * is ever picked, printed, packed or held back at the shadow row itself. The
 * inventory push worker already skips these rows
 * (`locations.dao.ts` filters `.is('delegated_to_location_id', null)`), which
 * means any per-location setting an operator writes against one binds nothing.
 * Offering it as a column or a pick-list entry is therefore an invitation to
 * configure a thing that cannot come true.
 *
 * SCOPE — presentation only:
 * - The delegation is SET and DISPLAYED only in Settings > Locations, which
 *   reads `allLocations` (the unfiltered list) instead of `locations`. That
 *   page still lists the row, still shows its "Fulfilled by" badge, and can
 *   still re-target or clear the delegation.
 * - Nothing here deactivates, deletes or otherwise mutates a location row. The
 *   record stays intact; it merely stops being offered.
 * - This is not an authorisation control. The backend's org/location gates are
 *   unchanged and remain the authority.
 *
 * ABSENCE MEANS NOT DELEGATED. The field is optional on legacy cached DTOs, and
 * hiding a real location because a cached payload predates the column is much
 * worse than showing a shadow row for one refetch.
 */

/** Structural row shape, so every caller's location type satisfies it uncast. */
export interface MaybeDelegatedLocation {
  delegated_to_location_id?: string | null;
}

/**
 * True only when the row carries a non-empty delegation target. `null`,
 * `undefined` and `''` are all "not delegated" — an empty string is not a
 * location id, and treating it as one would hide a real location.
 */
export function isDelegatedLocation(location: MaybeDelegatedLocation): boolean {
  const target = location.delegated_to_location_id;
  return typeof target === 'string' && target.trim() !== '';
}

/**
 * Drop delegated rows, preserving input order.
 *
 * Returns the SAME array reference when nothing is delegated. That is not a
 * micro-optimisation: several consumers hand the result straight to a
 * `useMemo`/`useEffect` dependency list, and a fresh array identity on every
 * render is an infinite-loop generator.
 */
export function excludeDelegatedLocations<T extends MaybeDelegatedLocation>(locations: T[]): T[] {
  return locations.some(isDelegatedLocation)
    ? locations.filter((location) => !isDelegatedLocation(location))
    : locations;
}
